import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../services/api";
import Navbar from "../components/Navbar";
import { buildProductDetailsPath } from "../utils/commerce";
import "./ProductDetails.css";

const formatDiscountPercent = (value) => {
  const rate = Number(value || 0);
  if (Number.isNaN(rate)) return "0";
  return Number(rate.toFixed(2)).toString();
};

export default function ProductDetails() {
  const { id, category, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const title = product?.name
      ? `${product.name} | Balitah`
      : "Product Details | Balitah";
    document.title = title;

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    const shortDescription = String(product?.description || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 155);

    descriptionTag.setAttribute(
      "content",
      shortDescription ||
        "View product details, pricing, and availability on Balitah.",
    );
  }, [product]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const data = await api.getProduct(id);
        const nextProduct = data.data;

        if (nextProduct?._id) {
          const canonicalPath = buildProductDetailsPath(nextProduct);
          const hasSeoParams = Boolean(category) && Boolean(slug);
          if (!hasSeoParams || location.pathname !== canonicalPath) {
            navigate(canonicalPath, { replace: true });
          }
        }

        setProduct(nextProduct);
        setSelectedVariantIndex(0);
        setQuantity(1);

        // Handle both old string format and new object format for images
        const imageUrl = Array.isArray(nextProduct?.images)
          ? nextProduct.images[0]?.url || nextProduct.images[0]
          : nextProduct?.image || "";

        setActiveImage(imageUrl || "");
      } catch (error) {
        console.error("Failed to load product:", error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, category, slug, location.pathname, navigate]);

  const addToCart = async () => {
    if (availableStock <= 0) {
      alert("This item is out of stock");
      return;
    }

    if (quantity > availableStock) {
      alert(`Only ${availableStock} item(s) available in stock`);
      return;
    }

    setAdding(true);
    try {
      const selectedVariantName = String(selectedVariant?.name || "").trim();
      const response = await api.addToCart(id, quantity, selectedVariantName);

      if (!response?.success) {
        const message = String(response?.message || "").toLowerCase();
        if (
          message.includes("not logged in") ||
          message.includes("not authorized")
        ) {
          navigate("/login");
          return;
        }
        alert(response?.message || "Failed to add to cart");
        return;
      }

      alert("Added to cart successfully");
      navigate("/products");
    } catch (error) {
      console.error("Failed to add to cart:", error);
      alert(error.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="pd-page">
        <Navbar />
        <div className="pd-container">
          <div className="pd-loading">
            <div className="pd-spinner"></div>
            <p>Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pd-page">
        <Navbar />
        <div className="pd-container">
          <div className="pd-error">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h2>Product not found</h2>
            <p>
              The product you're looking for doesn't exist or has been removed.
            </p>
            <button
              className="pd-btn pd-btn-primary"
              onClick={() => navigate("/products")}
            >
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const galleryImages = Array.isArray(product.images)
    ? product.images.map((img) => (typeof img === "string" ? img : img.url))
    : product.image
      ? [product.image]
      : [];

  const mainImage = activeImage || galleryImages[0] || "";

  const selectableVariants =
    Array.isArray(product.variants) && product.variants.length > 0
      ? product.variants
      : [{ name: product.type || "Default", price: product.price }];

  const selectedVariant =
    selectableVariants[selectedVariantIndex] || selectableVariants[0];
  const hasRealVariants =
    Array.isArray(product.variants) && product.variants.length > 0;
  const availableStock = hasRealVariants
    ? Math.max(0, Number(selectedVariant?.stock || 0))
    : Math.max(0, Number(product.stock || 0));
  const discountRate = Math.min(
    100,
    Math.max(0, Number(product.discountRate || 0)),
  );
  const basePrice = Number(selectedVariant?.price ?? product.price ?? 0);
  const discountedPrice = basePrice * (1 - discountRate / 100);

  return (
    <div className="pd-page">
      <Navbar />

      <div className="pd-container">
        {/* Back Button */}
        <button className="pd-back-btn" onClick={() => navigate(-1)}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        <div className="pd-content">
          {/* Image Gallery */}
          <div className="pd-gallery">
            <div className="pd-main-image">
              {mainImage ? (
                <img src={mainImage} alt={product.name} />
              ) : (
                <div className="pd-no-image">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>No image</span>
                </div>
              )}
              {discountRate > 0 && (
                <span className="pd-discount-badge">
                  -{formatDiscountPercent(discountRate)}%
                </span>
              )}
            </div>

            {galleryImages.length > 1 && (
              <div className="pd-thumbnails">
                {galleryImages.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    className={`pd-thumb ${activeImage === image ? "active" : ""}`}
                    onClick={() => setActiveImage(image)}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="pd-info">
            {/* Categories */}
            {Array.isArray(product.categories) &&
              product.categories.length > 0 && (
                <div className="pd-categories">
                  {product.categories.map((cat, i) => (
                    <span key={i} className="pd-category-tag">
                      {cat}
                    </span>
                  ))}
                </div>
              )}

            <h1 className="pd-title">{product.name}</h1>

            {/* Price Display */}
            <div className="pd-pricing">
              {discountRate > 0 ? (
                <>
                  <span className="pd-price-current">
                    BND {discountedPrice.toFixed(2)}
                  </span>
                  <span className="pd-price-original">
                    BND {basePrice.toFixed(2)}
                  </span>
                  <span className="pd-price-save">
                    Save {formatDiscountPercent(discountRate)}%
                  </span>
                </>
              ) : (
                <span className="pd-price-current">
                  BND {basePrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Variant Selector */}
            {selectableVariants.length > 1 && (
              <div className="pd-variant-section">
                <label className="pd-label">Select Variant</label>
                <div className="pd-variant-options">
                  {selectableVariants.map((variant, index) => (
                    <button
                      key={`${variant.name}-${index}`}
                      className={`pd-variant-btn ${selectedVariantIndex === index ? "active" : ""}`}
                      onClick={() => {
                        setSelectedVariantIndex(index);
                        setQuantity(1);
                      }}
                    >
                      <span className="pd-variant-name">
                        {variant.name || `Variant ${index + 1}`}
                      </span>
                      <span className="pd-variant-price">
                        BND {Number(variant.price || 0).toFixed(2)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className={`pd-stock-info ${availableStock > 0 ? "in" : "out"}`}>
              {availableStock > 0
                ? `${availableStock} item(s) available`
                : "Out of stock"}
            </p>

            {/* Quantity */}
            <div className="pd-quantity-section">
              <label className="pd-label">Quantity</label>
              <div className="pd-quantity-controls">
                <button
                  className="pd-qty-btn"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  disabled={quantity <= 1}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
                <span className="pd-qty-value">{quantity}</span>
                <button
                  className="pd-qty-btn"
                  onClick={() =>
                    setQuantity((prev) => Math.min(availableStock, prev + 1))
                  }
                  disabled={availableStock <= 0 || quantity >= availableStock}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pd-actions">
              <button
                className="pd-btn pd-btn-cart"
                onClick={addToCart}
                disabled={adding || availableStock <= 0}
              >
                {adding ? (
                  <>
                    <div className="pd-btn-spinner"></div>
                    Adding...
                  </>
                ) : availableStock <= 0 ? (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="8" y1="8" x2="16" y2="16" />
                      <line x1="16" y1="8" x2="8" y2="16" />
                    </svg>
                    Out of Stock
                  </>
                ) : (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="9" cy="21" r="1" />
                      <circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                    Add to Cart
                  </>
                )}
              </button>
              <button className="pd-btn pd-btn-buy">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
                Buy Now
              </button>
            </div>

            {/* Product Highlights */}
            <div className="pd-highlights">
              <div className="pd-highlight">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="1" y="3" width="15" height="13" />
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                <span>Free Delivery</span>
              </div>
              <div className="pd-highlight">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>Secure Payment</span>
              </div>
              <div className="pd-highlight">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <polyline points="7 23 3 19 7 15" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
                <span>Easy Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="pd-description-section">
          <h2>Product Description</h2>
          <p>
            {product.description?.trim() ||
              "No description available for this product."}
          </p>
        </div>
      </div>
    </div>
  );
}
